import { useOutletContext } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { Box, Grid, Tag, Text } from '@factorialco/deprecated-design-system'
import { AtsJobApplicationOtherInfoPageDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { CandidateSourceTag } from 'modules/ats/components/CandidateSourceTag'
import { CandidateTags } from 'modules/ats/components/CandidateTags'
import { useCustomCandidateSourceFeatureAvailability } from 'modules/ats/hooks/features/useCustomCandidateSourceFeatureAvailability'
import { timeAgoWhenRecent } from 'modules/ats/lib/dates'
import { isConsentExpired } from 'modules/ats/lib/utils/candidate'
import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'
import { getGenderLabel } from 'modules/ats/lib/utils/gender'

import { JobApplicationOtherInfoSkeleton } from './Skeleton'

interface OutletContext {
  isReviewerRole: boolean
}

const JobApplicationOtherInfoPage = () => {
  const { isReviewerRole } = useOutletContext<OutletContext>()
  const params = useParams()
  const id = String(params.application_id)
  const isCustomCandidateSourceFeatureAvailable = useCustomCandidateSourceFeatureAvailability()

  const { data, loading } = useQuery(AtsJobApplicationOtherInfoPageDocument, {
    variables: { id: id },
  })

  if (isReviewerRole) {
    return null
  }

  if (loading || !data?.ats.application) {
    return <JobApplicationOtherInfoSkeleton />
  }

  const { applicationSource, createdAt, candidate, medium } = data?.ats.application

  const isReferred = applicationSource?.name === 'referred'
  const consentText = candidate.consentExpirationDate
    ? isConsentExpired(candidate.consentExpirationDate)
      ? i18n.t('ats.application_details.side_content.data_consent.consent_expired_on', {
          date: dateBackendDateTimeToUI(candidate.consentExpirationDate),
        })
      : candidate.consentExpirationDate
    : i18n.t('ats.application_details.side_content.data_consent.no_consent')

  const otherInfoItems = [
    {
      title: `${i18n.t('ats.application_details.side_content.gender_label')}:`,
      text: getGenderLabel(candidate.gender),
    },
    {
      title: `${i18n.t('ats.application_details.side_content.added_label')}:`,
      text: timeAgoWhenRecent(createdAt),
    },
    {
      title: `${i18n.t('ats.application_details.side_content.consent_label')}:`,
      text: consentText,
    },
  ]
  return (
    <Box gap="s24" padding="s24">
      <Text.Normal color="textPrimary" weight="semibold">
        {i18n.t('ats.application_details.side_content.personal_info_title')}
      </Text.Normal>

      <Box
        gap="s24"
        paddingBottom="s24"
        paddingTop="s16"
        border={{ bottom: { style: 'dashed', width: 's1', color: 'grey400' } }}
      >
        {otherInfoItems.map((item, index) => (
          <Grid variant="fixed" key={`other-info-item-${index}`}>
            <Box gridColSpan={{ xs: 3, s: 3 }}>
              <Text.Normal>{item.title}</Text.Normal>
            </Box>
            <Box gridColSpan={{ xs: 9, s: 9 }}>
              <Text.Normal color="textPrimary">{item.text}</Text.Normal>
            </Box>
          </Grid>
        ))}
        <Grid variant="fixed">
          <Box gridColSpan={{ xs: 3, s: 3 }}>
            <Text.Normal>{i18n.t('ats.application_details.side_content.source_label')}</Text.Normal>
          </Box>
          <Box gridColSpan={{ xs: 9, s: 9 }} alignItems="flexStart">
            {isCustomCandidateSourceFeatureAvailable ? (
              <CandidateSourceTag application={data?.ats.application} />
            ) : (
              <Tag type={isReferred ? 'radical' : 'viridian'} inline>
                {getSourceLabel(applicationSource?.name || '')}
              </Tag>
            )}
            {isReferred && !!medium && (
              <Text.Normal size="200">
                {i18n.t('ats.candidates.application_view.referred_by', {
                  full_name: medium,
                })}
              </Text.Normal>
            )}
          </Box>
        </Grid>
      </Box>

      <Text.Normal color="textPrimary" weight="semibold">
        {i18n.t('ats.application_details.side_content.tags_title')}
      </Text.Normal>
      <CandidateTags candidate={candidate} source="Application" />
    </Box>
  )
}

export default JobApplicationOtherInfoPage
